import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Eye, Heart, MessageCircle, Play, Share2, TrendingUp, X } from 'lucide-react';
import Nav from '../components/Nav';
import LoadingSpinner from '../components/LoadingSpinner';
import { API_BASE, ROUTES } from '../../shared/constants';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';

// Chart components
import ChartCard from '../components/ui/ChartCard';
import BaseChart from '../components/ui/BaseChart';
import { formatLargeNumber, normalizeValue, generatePolylinePoints, getTooltipTransform } from '../components/ui/chartUtils';

interface VideoData {
  video: {
    id: string;
    tiktok_id?: string;
    title: string;
    cover_image_url?: string;
    posted_at: string;
    created_at: string;
    view_count: number;
    like_count: number;
    comment_count: number;
    share_count: number;
    engagement_rate: number;
  };
  account: {
    username: string;
    name: string;
    avatar_url?: string;
  } | null;
  snapshots: {
    views: Array<{ timestamp: string; value: number }>;
    likes: Array<{ timestamp: string; value: number }>;
    comments: Array<{ timestamp: string; value: number }>;
    shares: Array<{ timestamp: string; value: number }>;
    engagement: Array<{ timestamp: string; value: number }>;
  };
}

/**
 * Video Detail Page - Individual video performance analytics
 * Shows time-series charts for all video metrics
 */
export default function VideoDetailPage() {
  const { username, videoId } = useParams<{ username: string; videoId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [data, setData] = useState<VideoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEmbed, setShowEmbed] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/analytics/video/${videoId}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to load video data');
        }

        const videoData = await response.json();
        setData(videoData);
      } catch (err) {
        console.error('Error fetching video data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load video data');
      } finally {
        setIsLoading(false);
      }
    };

    if (videoId) {
      fetchVideoData();
    }
  }, [videoId]);

  const handleBack = () => {
    navigate(`/dashboard/${username}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Nav email={user?.email} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Nav email={user?.email} onLogout={handleLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error || 'Video not found'}</p>
            <button
              onClick={handleBack}
              className="text-primary-400 hover:text-primary-300 transition"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { video, account, snapshots } = data;
  const formatNumber = (num: number) => num.toLocaleString();
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="min-h-screen bg-slate-950">
      <Nav email={user?.email} onLogout={handleLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Dashboard</span>
        </button>

        {/* Video Header */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Thumbnail - click to play TikTok video */}
            {video.cover_image_url && (
              <button
                onClick={() => {
                  if (video.tiktok_id) {
                    setShowEmbed(true);
                  } else {
                    // Fallback: open TikTok profile
                    window.open(`https://www.tiktok.com/@${account?.username || username}`, '_blank');
                  }
                }}
                className="relative w-full md:w-48 h-48 flex-shrink-0 rounded-lg overflow-hidden bg-slate-800 group cursor-pointer"
              >
                <img
                  src={video.cover_image_url}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                    <Play className="w-7 h-7 text-white ml-0.5" fill="white" />
                  </div>
                </div>
              </button>
            )}

            {/* Metadata */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-2">{video.title}</h1>
              
              {account && (
                <div className="flex items-center gap-2 text-slate-400 mb-4">
                  <span className="text-primary-400">@{account.username}</span>
                  <span>•</span>
                  <span>{formatDate(video.posted_at)}</span>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs uppercase font-bold tracking-wide">Views</span>
                  </div>
                  <span className="text-lg font-bold text-white">{formatNumber(video.view_count)}</span>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Heart className="w-4 h-4" />
                    <span className="text-xs uppercase font-bold tracking-wide">Likes</span>
                  </div>
                  <span className="text-lg font-bold text-white">{formatNumber(video.like_count)}</span>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-xs uppercase font-bold tracking-wide">Comments</span>
                  </div>
                  <span className="text-lg font-bold text-white">{formatNumber(video.comment_count)}</span>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Share2 className="w-4 h-4" />
                    <span className="text-xs uppercase font-bold tracking-wide">Shares</span>
                  </div>
                  <span className="text-lg font-bold text-white">{formatNumber(video.share_count)}</span>
                </div>
              </div>

              {/* Engagement Rate */}
              <div className="mt-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary-400" />
                <span className="text-slate-400">Engagement Rate:</span>
                <span className="text-lg font-bold text-primary-400">{video.engagement_rate.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <VideoChart
            title="Views Over Time"
            data={snapshots.views}
            color="#38bdf8"
            metricName="views"
          />
          
          <VideoChart
            title="Likes Over Time"
            data={snapshots.likes}
            color="#fb7185"
            metricName="likes"
          />
          
          <VideoChart
            title="Comments Over Time"
            data={snapshots.comments}
            color="#a78bfa"
            metricName="comments"
          />
          
          <VideoChart
            title="Shares Over Time"
            data={snapshots.shares}
            color="#34d399"
            metricName="shares"
          />
          
          <VideoChart
            title="Engagement Rate Over Time"
            data={snapshots.engagement}
            color="#fbbf24"
            metricName="engagement"
            isPercentage
          />
        </div>
      </div>

      {/* TikTok Video Embed Modal */}
      {showEmbed && video.tiktok_id && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowEmbed(false)}
        >
          <div
            className="relative w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowEmbed(false)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white transition z-10"
            >
              <X className="w-8 h-8" />
            </button>

            {/* TikTok Embed iframe */}
            <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{ aspectRatio: '9/16' }}>
              <iframe
                src={`https://www.tiktok.com/embed/v2/${video.tiktok_id}`}
                className="w-full h-full"
                allowFullScreen
                allow="encrypted-media"
                style={{ border: 'none' }}
              />
            </div>

            {/* Open in TikTok link */}
            <a
              href={`https://www.tiktok.com/@${account?.username || username}/video/${video.tiktok_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-300 hover:text-white transition"
            >
              <ExternalLink className="w-4 h-4" />
              Open in TikTok
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

interface VideoChartProps {
  title: string;
  data: Array<{ timestamp: string; value: number }>;
  color: string;
  metricName: string;
  isPercentage?: boolean;
}

function VideoChart({ title, data, color, metricName, isPercentage = false }: VideoChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return (
      <ChartCard title={title} isLoading={false}>
        <div className="h-48 flex items-center justify-center text-slate-500">
          No data available
        </div>
      </ChartCard>
    );
  }

  const dataPoints = data.map(d => d.value);
  const maxVal = Math.max(...dataPoints, 0);
  const minVal = Math.min(...dataPoints, 0);

  const width = 1000;
  const height = 200;
  const topPadding = 20;
  const bottomPadding = 20;

  const normalizeY = (val: number) => 
    normalizeValue(val, minVal, maxVal, height, topPadding, bottomPadding);

  const points = generatePolylinePoints(dataPoints, width, normalizeY);

  // Generate X-axis labels (show first, middle, last)
  const xAxisLabels = data.length > 0 ? [
    { index: 0, text: new Date(data[0].timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) },
    { index: Math.floor(data.length / 2), text: new Date(data[Math.floor(data.length / 2)].timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) },
    { index: data.length - 1, text: new Date(data[data.length - 1].timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) },
  ] : [];

  // Generate Y-axis labels
  const yAxisLabels = [
    { value: maxVal, text: isPercentage ? `${maxVal.toFixed(1)}%` : formatLargeNumber(maxVal) },
    { value: (maxVal + minVal) / 2, text: isPercentage ? `${((maxVal + minVal) / 2).toFixed(1)}%` : formatLargeNumber((maxVal + minVal) / 2) },
    { value: minVal, text: isPercentage ? `${minVal.toFixed(1)}%` : formatLargeNumber(minVal) }
  ];

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const relativeX = x / rect.width;
    const closestIndex = Math.round(relativeX * (dataPoints.length - 1));
    setHoveredPoint(Math.max(0, Math.min(dataPoints.length - 1, closestIndex)));
  };

  const legend = (
    <div className="flex items-center gap-1.5">
      <div className="w-3 h-0.5" style={{ backgroundColor: color }}></div>
      <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{metricName}</span>
    </div>
  );

  const growth = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] - dataPoints[0] : 0;
  const actions = (
    <div className="text-right">
      <span className={`text-2xl font-bold ${growth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
        {growth >= 0 ? '+' : ''}{isPercentage ? growth.toFixed(2) + '%' : growth.toLocaleString()}
      </span>
      <p className="text-xs text-slate-400 uppercase tracking-wide">{metricName}</p>
    </div>
  );

  const overlays = (
    <>
      {/* Hover dot */}
      {hoveredPoint !== null && (
        <div className="absolute inset-0 pointer-events-none z-20">
          <div
            className="absolute rounded-full border-2 border-white shadow-sm"
            style={{
              backgroundColor: color,
              left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
              top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
              width: '12px',
              height: '12px',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </div>
      )}

      {/* Tooltip */}
      {hoveredPoint !== null && (
        <div
          className="absolute bg-slate-800 text-white text-xs px-2 py-1.5 rounded border border-slate-700 shadow-2xl pointer-events-none z-40"
          style={{
            left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
            top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
            transform: getTooltipTransform(hoveredPoint, dataPoints.length, '-45px')
          }}
        >
          <div className="font-bold">
            {isPercentage ? dataPoints[hoveredPoint].toFixed(2) + '%' : dataPoints[hoveredPoint].toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400">
            {new Date(data[hoveredPoint].timestamp).toLocaleDateString([], { 
              month: 'short', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      )}
    </>
  );

  return (
    <ChartCard
      title={title}
      legend={legend}
      actions={actions}
      isLoading={false}
    >
      <BaseChart
        width={width}
        height={height}
        topPadding={topPadding}
        bottomPadding={bottomPadding}
        xAxisLabels={xAxisLabels}
        yAxisLabels={yAxisLabels}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
        overlays={overlays}
      >
        {/* Main line */}
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="3"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </BaseChart>
    </ChartCard>
  );
}
